import React, { useState, Children } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, CheckCircle2Icon, AlertCircleIcon, ZapIcon, BrainCircuitIcon, ShieldCheckIcon, ClockIcon, BookOpenIcon, LockIcon, XIcon } from 'lucide-react';
import { SocialProofPopup } from '../components/SocialProofPopup';
import { ContactModal } from '../components/ContactModal';
// Animation variants
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
export function LandingPage() {
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const scrollToCheckout = () => {
    const element = document.getElementById('checkout');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <div className="min-h-screen bg-[#FAFAF8] text-[#1C1C2E] font-sans selection:bg-[#0ABFBC]/20" data-id="element-44">
      {/* 1. STICKY HEADER */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-white/90 backdrop-blur-md border-b border-gray-100 shadow-sm" data-id="element-45">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between" data-id="element-46">
          <div className="flex items-center" data-id="element-47">
            <span className="text-lg font-bold tracking-tight text-[#0ABFBC]" data-id="element-48">
              ANXIETY OFF SWITCH™
            </span>
          </div>
          <button onClick={scrollToCheckout} className="hidden sm:inline-flex items-center justify-center px-5 py-2 text-sm font-semibold text-white transition-all bg-[#0ABFBC] rounded-full hover:bg-[#08a8a5] hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0ABFBC]" data-id="element-49">
            Get Instant Relief Now
          </button>
          <button onClick={scrollToCheckout} className="sm:hidden inline-flex items-center justify-center px-4 py-2 text-xs font-semibold text-white bg-[#0ABFBC] rounded-full hover:bg-[#08a8a5]" data-id="element-50">
            Get Instant Relief Now
          </button>
        </div>
      </header>

      {/* 1. HERO SECTION */}
      <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-24 overflow-hidden" data-id="element-51">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-52">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-53">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeInUp} className="max-w-2xl" data-id="element-54">
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-teal-50 text-[#0ABFBC] text-xs font-bold uppercase tracking-wide mb-6" data-id="element-55">
                <ZapIcon className="w-3 h-3 mr-1.5" data-id="element-56" />
                Instant Digital Delivery
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#1C1C2E] leading-[1.1] mb-6" data-id="element-57">
                What if your anxiety could be shut off like a{' '}
                <span className="text-[#0ABFBC] relative inline-block" data-id="element-58">
                  switch?
                  <svg className="absolute w-full h-3 -bottom-1 left-0 text-[#0ABFBC]/20" viewBox="0 0 100 10" preserveAspectRatio="none" data-id="element-59">
                    <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="8" fill="none" data-id="element-60" />
                  </svg>
                </span>
              </h1>
              <p className="text-lg sm:text-xl text-gray-600 mb-8 leading-relaxed" data-id="element-61">
                Not meditation retreats. Not affirmations you don't believe. Not
                generic "just relax."
                <br className="hidden sm:block" data-id="element-62" />
                <span className="font-medium text-gray-900" data-id="element-63">
                  Discover micro-tools that work in 30 seconds to 2 minutes, in
                  real life moments.
                </span>
              </p>
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-64">
                <button onClick={scrollToCheckout} className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-white transition-all bg-[#0ABFBC] rounded-full hover:bg-[#08a8a5] hover:shadow-lg hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0ABFBC]" data-id="element-65">
                  Get Instant Relief Now
                  <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-66" />
                </button>
              </div>
              <p className="mt-4 text-sm text-gray-500 flex items-center" data-id="element-67">
                <CheckCircle2Icon className="w-4 h-4 mr-1.5 text-[#0ABFBC]" data-id="element-68" />
                Instant digital download
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="relative mx-auto lg:ml-auto w-full max-w-md lg:max-w-full" data-id="element-69">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-white p-2 sm:p-4 rotate-1 hover:rotate-0 transition-transform duration-500" data-id="element-70">
                <img src="https://cdn.magicpatterns.com/uploads/6qJFBP7XqKGpG1QTqP5Wry/nanobanana-2026-02-19T18-57-41.png" alt="Woman reading Anxiety Off Switch in a park" className="rounded-2xl w-full h-auto object-cover aspect-[4/3]" data-id="element-71" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent rounded-2xl pointer-events-none" data-id="element-72"></div>
                <div className="absolute bottom-6 left-6 right-6 text-white pointer-events-none" data-id="element-73">
                  <p className="font-medium text-sm text-white/90 uppercase tracking-wider mb-1" data-id="element-74">
                    Real Relief
                  </p>
                  <p className="font-bold text-lg" data-id="element-75">
                    "Finally, something that works when I'm actually panicking."
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 2. RELATABLE PROBLEM */}
      <section className="py-16 sm:py-24 bg-white" data-id="element-76">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-77">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="space-y-12" data-id="element-78">
            <div className="text-center mb-16" data-id="element-79">
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1C1C2E] mb-4" data-id="element-80">
                Does this sound familiar?
              </h2>
              <p className="text-lg text-gray-600" data-id="element-81">
                The modern anxiety loop keeps you stuck.
              </p>
            </div>

            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-6 items-start" data-id="element-82">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-50 flex items-center justify-center text-red-500" data-id="element-83">
                <AlertCircleIcon className="w-6 h-6" data-id="element-84" />
              </div>
              <div data-id="element-85">
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-86">
                  The Panic Surge
                </h3>
                <p className="text-gray-600 leading-relaxed mb-3" data-id="element-87">
                  That sudden tight chest, short breath, and wave of dread that
                  hits out of nowhere. You feel like you're losing control, and
                  "calm down" is the last thing you can do.
                </p>
              </div>
            </motion.div>

            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-6 items-start" data-id="element-88">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-orange-50 flex items-center justify-center text-orange-500" data-id="element-89">
                <BrainCircuitIcon className="w-6 h-6" data-id="element-90" />
              </div>
              <div data-id="element-91">
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-92">
                  The Overthinking Spiral
                </h3>
                <p className="text-gray-600 leading-relaxed mb-3" data-id="element-93">
                  "What if..." scenarios playing on repeat. Catastrophizing
                  about the future. Replaying awkward conversations from 3 years
                  ago at 2 AM.
                </p>
              </div>
            </motion.div>

            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-6 items-start" data-id="element-94">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-purple-50 flex items-center justify-center text-purple-500" data-id="element-95">
                <ClockIcon className="w-6 h-6" data-id="element-96" />
              </div>
              <div data-id="element-97">
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-98">
                  The Doomscrolling Loop
                </h3>
                <p className="text-gray-600 leading-relaxed mb-3" data-id="element-99">
                  You pick up your phone for "just 5 minutes" to distract
                  yourself. One hour later, you feel heavier, more anxious, and
                  paralyzed.
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* 3. REFRAME / VALIDATION */}
      <section className="py-16 sm:py-24 bg-[#FAFAF8] border-y border-gray-100" data-id="element-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-101">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} data-id="element-102">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1C1C2E] mb-6" data-id="element-103">
              You're not broken. <br className="sm:hidden" data-id="element-104" />
              You're overloaded.
            </h2>
            <div className="space-y-6 text-lg text-gray-600 leading-relaxed" data-id="element-105">
              <p data-id="element-106">
                Anxiety is fast. It hits in milliseconds. Traditional tools like
                hour-long meditation or therapy sessions are powerful, but they
                can't always help in the{' '}
                <span className="font-semibold text-gray-900" data-id="element-107">
                  exact moment
                </span>{' '}
                you're spiraling.
              </p>
              <p data-id="element-108">
                When threat mode activates, your logic center shuts down. Your
                nervous system takes over. You can't "think" your way out of a
                physiological response.
              </p>
              <p className="font-medium text-[#0ABFBC] text-xl" data-id="element-109">
                You need tools that speak the language of your nervous system.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 4. THE SOLUTION */}
      <section className="py-16 sm:py-24 bg-white overflow-hidden" data-id="element-110">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-111">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-112">
            <motion.div initial={{
            opacity: 0,
            x: -50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="order-2 lg:order-1" data-id="element-113">
              <div className="relative" data-id="element-114">
                <div className="absolute -inset-4 bg-[#0ABFBC]/10 rounded-full blur-3xl" data-id="element-115"></div>
                <img src="https://cdn.magicpatterns.com/uploads/nhv8YVLMYTydfx6FYY8fXH/heylloyd-image-1771525709435.png" alt="Anxiety Off Switch Ebook Cover" className="relative mx-auto w-full max-w-sm drop-shadow-2xl hover:scale-105 transition-transform duration-500" data-id="element-116" />
              </div>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={staggerContainer} className="order-1 lg:order-2" data-id="element-117">
              <motion.div variants={fadeInUp} data-id="element-118">
                <h2 className="text-3xl sm:text-4xl font-bold text-[#1C1C2E] mb-6" data-id="element-119">
                  Introducing{' '}
                  <span className="text-[#0ABFBC]" data-id="element-120">ANXIETY OFF SWITCH™</span>
                </h2>
                <p className="text-xl text-gray-600 mb-8" data-id="element-121">
                  A portable nervous system reset guide designed for the modern,
                  overstimulated world.
                </p>
              </motion.div>

              <motion.ul variants={staggerContainer} className="space-y-4 mb-8" data-id="element-122">
                <motion.li variants={fadeInUp} className="flex items-start" data-id="element-123">
                  <CheckCircle2Icon className="w-6 h-6 text-[#0ABFBC] mr-3 flex-shrink-0 mt-0.5" data-id="element-124" />
                  <span className="text-gray-700 text-lg" data-id="element-125">
                    Micro-tools built for the 30 hardest seconds of a spike.
                  </span>
                </motion.li>
                <motion.li variants={fadeInUp} className="flex items-start" data-id="element-126">
                  <CheckCircle2Icon className="w-6 h-6 text-[#0ABFBC] mr-3 flex-shrink-0 mt-0.5" data-id="element-127" />
                  <span className="text-gray-700 text-lg" data-id="element-128">
                    A simple framework:{' '}
                    <span className="font-bold" data-id="element-129">
                      Interrupt → Redirect → Rewire
                    </span>
                    .
                  </span>
                </motion.li>
                <motion.li variants={fadeInUp} className="flex items-start" data-id="element-130">
                  <CheckCircle2Icon className="w-6 h-6 text-[#0ABFBC] mr-3 flex-shrink-0 mt-0.5" data-id="element-131" />
                  <span className="text-gray-700 text-lg" data-id="element-132">
                    No fluff. No long theories. Just tactics that work.
                  </span>
                </motion.li>
              </motion.ul>

              <motion.button variants={fadeInUp} onClick={scrollToCheckout} className="text-[#0ABFBC] font-bold text-lg hover:text-[#08a8a5] flex items-center group" data-id="element-133">
                Jump to instant access
                <ArrowRightIcon className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-134" />
              </motion.button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 5. WHAT'S INSIDE */}
      <section className="py-16 sm:py-24 bg-[#FAFAF8]" data-id="element-135">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-136">
          <div className="text-center mb-12" data-id="element-137">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1C1C2E] mb-4" data-id="element-138">
              What's Inside The Guide
            </h2>
            <p className="text-lg text-gray-600" data-id="element-139">
              This isn't a book to read. It's a book to use.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6" data-id="element-140">
            {['Micro-tools for panic, overthinking, doomscrolling', 'Somatic techniques for physical anxiety spikes', 'Rapid cognitive interrupters', '10-, 30-, and 60-second protocols', 'Real-life examples + step-by-step instructions', '21-day reset plan for consistency'].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-start" data-id="element-141">
                <BookOpenIcon className="w-5 h-5 text-[#0ABFBC] mr-3 mt-1 flex-shrink-0" data-id="element-142" />
                <span className="text-gray-800 font-medium" data-id="element-143">{item}</span>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* 6. WHY IT WORKS */}
      <section className="py-16 sm:py-24 bg-white" data-id="element-144">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-145">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#1C1C2E] mb-12" data-id="element-146">
            Why It Works
          </h2>

          <div className="grid md:grid-cols-2 gap-8 text-left" data-id="element-147">
            <div className="bg-gray-50 p-8 rounded-2xl" data-id="element-148">
              <ShieldCheckIcon className="w-10 h-10 text-[#0ABFBC] mb-4" data-id="element-149" />
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-150">
                Safety Signals Beat Logic
              </h3>
              <p className="text-gray-600" data-id="element-151">
                During an anxiety spike, your amygdala (fear center) is
                screaming. Logic can't hear you. These tools send physical
                safety signals that your body understands instantly.
              </p>
            </div>
            <div className="bg-gray-50 p-8 rounded-2xl" data-id="element-152">
              <ZapIcon className="w-10 h-10 text-[#0ABFBC] mb-4" data-id="element-153" />
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-154">
                Body-First Downshifting
              </h3>
              <p className="text-gray-600" data-id="element-155">
                We use "bottom-up" processing. By calming the body first, the
                mind naturally follows. It's faster, more reliable, and doesn't
                require willpower.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 7. WHO IT'S FOR */}
      <section className="py-16 sm:py-24 bg-[#1C1C2E] text-white" data-id="element-156">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-157">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12" data-id="element-158">
            Who This Is For
          </h2>

          <div className="grid sm:grid-cols-2 gap-4" data-id="element-159">
            {['Overstimulated minds needing quiet', 'People who spiral at night', "Doomscrollers who can't stop", 'High-performers with hidden anxiety', 'Anyone wanting fast, discreet tools', "People tired of 'just breathe' advice"].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="flex items-center p-4 rounded-lg bg-white/5 border border-white/10" data-id="element-160">
                <CheckCircle2Icon className="w-5 h-5 text-[#0ABFBC] mr-3" data-id="element-161" />
                <span className="text-lg font-medium" data-id="element-162">{item}</span>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* 8. FAQ */}
      <section className="py-16 sm:py-24 bg-white" data-id="element-163">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-164">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#1C1C2E] mb-12 text-center" data-id="element-165">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6" data-id="element-166">
            {[{
            q: 'How fast are the tools?',
            a: "Most tools are designed to work in 30 seconds to 2 minutes. They are 'micro-tools' meant for immediate intervention."
          }, {
            q: 'Do I need meditation experience?',
            a: 'Zero. None of these require you to clear your mind or sit still for 20 minutes. They are active, practical tools.'
          }, {
            q: 'Is this therapy?',
            a: 'No. This is an educational guide with practical self-regulation tools. It complements therapy but does not replace professional care.'
          }, {
            q: 'Will it eliminate anxiety forever?',
            a: "Anxiety is a natural human emotion. The goal isn't elimination, but regulation—turning the volume down so you stay in control."
          }].map((item, i) => <div key={i} className="border-b border-gray-100 pb-6 last:border-0" data-id="element-167">
                <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-168">
                  {item.q}
                </h3>
                <p className="text-gray-600" data-id="element-169">{item.a}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 9. DISCLAIMER */}
      <section className="py-12 bg-gray-50 border-y border-gray-200" data-id="element-170">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-171">
          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-172">
            <div className="flex items-start" data-id="element-173">
              <AlertCircleIcon className="w-6 h-6 text-gray-400 mr-4 flex-shrink-0 mt-1" data-id="element-174" />
              <div data-id="element-175">
                <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-176">
                  Safety & Scope Disclaimer
                </h3>
                <p className="text-sm text-gray-500 leading-relaxed" data-id="element-177">
                  The content in ANXIETY OFF SWITCH™ is for educational
                  purposes only and does not constitute medical advice,
                  diagnosis, or treatment. These tools are not a replacement for
                  professional mental health care or emergency services. If you
                  are experiencing severe, frequent, or worsening symptoms, or
                  have any safety concerns, please contact a licensed mental
                  health professional or emergency services immediately.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 10. CHECKOUT SECTION */}
      <section id="checkout" className="py-16 sm:py-24 bg-white scroll-mt-20" data-id="element-178">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-179">
          <div className="bg-[#FAFAF8] rounded-3xl shadow-xl overflow-hidden border border-gray-100" data-id="element-180">
            <div className="grid lg:grid-cols-2" data-id="element-181">
              {/* Left: Image */}
              <div className="bg-gray-100 p-8 lg:p-12 flex items-center justify-center" data-id="element-182">
                <img src="https://cdn.magicpatterns.com/uploads/nhv8YVLMYTydfx6FYY8fXH/heylloyd-image-1771525709435.png" alt="Anxiety Off Switch Complete Package" className="w-full max-w-sm drop-shadow-2xl" data-id="element-183" />
              </div>

              {/* Right: Offer */}
              <div className="p-8 lg:p-12 flex flex-col justify-center" data-id="element-184">
                <div className="mb-6" data-id="element-185">
                  <h2 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-186">
                    Get Instant Access
                  </h2>
                  <p className="text-gray-600" data-id="element-187">
                    Start your reset in the next 2 minutes.
                  </p>
                </div>

                <div className="mb-8" data-id="element-188">
                  <div className="flex items-baseline mb-4" data-id="element-189">
                    <span className="text-5xl font-extrabold text-[#1C1C2E]" data-id="element-190">
                      $6.36
                    </span>
                    <span className="ml-2 text-gray-500 line-through text-xl" data-id="element-191">
                      $19.99
                    </span>
                  </div>

                  <ul className="space-y-3 mb-8" data-id="element-192">
                    <li className="flex items-center text-gray-700" data-id="element-193">
                      <CheckCircle2Icon className="w-5 h-5 text-[#0ABFBC] mr-3" data-id="element-194" />
                      <span data-id="element-195">Complete Digital Ebook</span>
                    </li>
                    <li className="flex items-center text-gray-700" data-id="element-196">
                      <CheckCircle2Icon className="w-5 h-5 text-[#0ABFBC] mr-3" data-id="element-197" />
                      <span data-id="element-198">21-Day Reset Plan</span>
                    </li>
                    <li className="flex items-center text-gray-700" data-id="element-199">
                      <CheckCircle2Icon className="w-5 h-5 text-[#0ABFBC] mr-3" data-id="element-200" />
                      <span data-id="element-201">Instant PDF Download</span>
                    </li>
                    <li className="flex items-center text-gray-700" data-id="element-202">
                      <CheckCircle2Icon className="w-5 h-5 text-[#0ABFBC] mr-3" data-id="element-203" />
                      <span data-id="element-204">Lifetime Access & Updates</span>
                    </li>
                  </ul>

                  <a href="https://www.dreambuk.com/cart/45415325434042:1" className="block w-full py-4 px-6 text-center bg-[#0ABFBC] hover:bg-[#08a8a5] text-white font-bold text-xl rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200" data-id="element-205">
                    Get Instant Access — $6.36
                  </a>

                  <div className="mt-4 flex items-center justify-center text-xs text-gray-500 gap-4" data-id="element-206">
                    <span className="flex items-center" data-id="element-207">
                      <LockIcon className="w-3 h-3 mr-1" data-id="element-208" /> Secure Checkout
                    </span>
                    <span className="flex items-center" data-id="element-209">
                      <ZapIcon className="w-3 h-3 mr-1" data-id="element-210" /> Instant Delivery
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 11. FINAL CLOSE */}
      <section className="py-16 bg-white text-center" data-id="element-211">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-212">
          <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-213">
            Start Small. Regain Control.
          </h2>
          <p className="text-gray-600 mb-8" data-id="element-214">
            You don't need to fix everything today. Just start with a 10%
            reduction. Then another. These tools give you the power to turn the
            volume down whenever you need to.
          </p>
          <a href="https://www.dreambuk.com/cart/45415325434042:1" className="inline-flex items-center text-[#0ABFBC] font-bold hover:underline" data-id="element-215">
            Get your copy now
            <ArrowRightIcon className="ml-1 w-4 h-4" data-id="element-216" />
          </a>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-gray-50 border-t border-gray-200 py-12" data-id="element-217">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-218">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-8 mb-8" data-id="element-219">
            <div className="flex-shrink-0" data-id="element-220">
              <span className="text-lg font-bold tracking-tight text-[#0ABFBC] mb-3 block" data-id="element-221">
                ANXIETY OFF SWITCH™
              </span>
              <p className="text-sm text-gray-500 max-w-xs" data-id="element-222">
                Micro-tools for a calmer, clearer mind.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-8 text-sm text-gray-600" data-id="element-223">
              <button onClick={() => setIsPrivacyOpen(true)} className="hover:text-[#0ABFBC] transition-colors font-medium" data-id="element-224">
                Privacy Policy
              </button>
              <button onClick={() => setIsTermsOpen(true)} className="hover:text-[#0ABFBC] transition-colors font-medium" data-id="element-225">
                Terms of Service
              </button>
              <button onClick={() => setIsContactOpen(true)} className="hover:text-[#0ABFBC] transition-colors font-medium" data-id="element-226">
                Contact Support
              </button>
            </div>
          </div>
          <div className="pt-6 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-gray-400" data-id="element-227">
            <span data-id="element-228">
              &copy; {new Date().getFullYear()} Dreambuk. All rights reserved.
            </span>
            <span data-id="element-229">
              ANXIETY OFF SWITCH™ is an educational resource. Not medical
              advice.
            </span>
          </div>
        </div>
      </footer>

      <SocialProofPopup data-id="element-230" />
      <ContactModal isOpen={isContactOpen} onClose={() => setIsContactOpen(false)} data-id="element-231" />

      {/* PRIVACY POLICY MODAL */}
      {isPrivacyOpen && <>
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60]" onClick={() => setIsPrivacyOpen(false)} data-id="element-232" />
          <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl z-[70] p-4 max-h-[90vh] overflow-y-auto" data-id="element-233">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden" data-id="element-234">
              <div className="p-6 sm:p-8" data-id="element-235">
                <div className="flex justify-between items-center mb-6" data-id="element-236">
                  <h3 className="text-xl font-bold text-gray-900" data-id="element-237">
                    Privacy Policy
                  </h3>
                  <button onClick={() => setIsPrivacyOpen(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500" data-id="element-238">
                    <XIcon className="w-5 h-5" data-id="element-239" />
                  </button>
                </div>
                <div className="prose prose-sm text-gray-600 space-y-4" data-id="element-240">
                  <p className="text-xs text-gray-400" data-id="element-241">
                    Last updated: {new Date().getFullYear()}
                  </p>
                  <h4 className="font-bold text-gray-900" data-id="element-242">
                    1. Data We Collect
                  </h4>
                  <p data-id="element-243">
                    We collect information you provide directly to us, such as
                    your name and email address when you make a purchase. We
                    also collect usage data automatically, including IP address,
                    browser type, and pages visited.
                  </p>
                  <h4 className="font-bold text-gray-900" data-id="element-244">
                    2. How We Use Your Data
                  </h4>
                  <p data-id="element-245">
                    We use your data to process transactions, deliver your
                    digital product, send order confirmations, and improve our
                    services. We do not sell your personal information to third
                    parties.
                  </p>
                  <h4 className="font-bold text-gray-900" data-id="element-246">3. Cookies</h4>
                  <p data-id="element-247">
                    We use cookies to enhance your browsing experience, analyze
                    site traffic, and personalize content. You may disable
                    cookies in your browser settings, though some features may
                    not function properly.
                  </p>
                  <h4 className="font-bold text-gray-900" data-id="element-248">
                    4. GDPR Compliance
                  </h4>
                  <p data-id="element-249">
                    If you are located in the European Economic Area (EEA), you
                    have the right to access, rectify, or erase your personal
                    data. You may also object to or restrict processing of your
                    data. To exercise these rights, contact us at
                    info@dreambuk.com.
                  </p>
                  <h4 className="font-bold text-gray-900" data-id="element-250">5. Data Retention</h4>
                  <p data-id="element-251">
                    We retain your personal data only as long as necessary to
                    fulfill the purposes outlined in this policy or as required
                    by law.
                  </p>
                  <h4 className="font-bold text-gray-900" data-id="element-252">6. Contact</h4>
                  <p data-id="element-253">
                    For privacy-related inquiries, contact us at
                    info@dreambuk.com or via WhatsApp at +1 5512540741.
                  </p>
                </div>
              </div>
              <div className="bg-gray-50 px-6 py-4 text-center" data-id="element-254">
                <button onClick={() => setIsPrivacyOpen(false)} className="text-gray-500 hover:text-gray-900 text-sm font-medium transition-colors" data-id="element-255">
                  Close
                </button>
              </div>
            </div>
          </div>
        </>}

      {/* TERMS OF SERVICE MODAL */}
      {isTermsOpen && <>
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60]" onClick={() => setIsTermsOpen(false)} data-id="element-256" />
          <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl z-[70] p-4 max-h-[90vh] overflow-y-auto" data-id="element-257">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden" data-id="element-258">
              <div className="p-6 sm:p-8" data-id="element-259">
                <div className="flex justify-between items-center mb-6" data-id="element-260">
                  <h3 className="text-xl font-bold text-gray-900" data-id="element-261">
                    Terms of Service
                  </h3>
                  <button onClick={() => setIsTermsOpen(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500" data-id="element-262">
                    <XIcon className="w-5 h-5" data-id="element-263" />
                  </button>
                </div>
                <div className="prose prose-sm text-gray-600 space-y-4" data-id="element-264">
                  <p className="text-xs text-gray-400" data-id="element-265">
                    Last updated: {new Date().getFullYear()}
                  </p>
                  <h4 className="font-bold text-gray-900" data-id="element-266">
                    1. User Responsibilities
                  </h4>
                  <p data-id="element-267">
                    By purchasing and using ANXIETY OFF SWITCH™, you agree to
                    use this product for personal, non-commercial purposes only.
                    You may not reproduce, distribute, or resell any part of
                    this ebook without written permission from Dreambuk.
                  </p>
                  <h4 className="font-bold text-gray-900" data-id="element-268">2. Payment Terms</h4>
                  <p data-id="element-269">
                    All purchases are processed securely through our payment
                    provider. Payment is due at the time of purchase. Prices are
                    listed in USD and are subject to change without notice.
                  </p>
                  <h4 className="font-bold text-gray-900" data-id="element-270">3. Refund Policy</h4>
                  <p data-id="element-271">
                    We want you to be completely satisfied. If you encounter an
                    issue, you must notify us within the first 7 days of
                    purchase. Once notified, and if eligible, refunds can be
                    processed up to 30 days from the purchase date.
                  </p>
                  <p className="font-medium text-gray-700" data-id="element-272">
                    Refunds apply in the following cases:
                  </p>
                  <ul className="list-disc pl-5 space-y-1" data-id="element-273">
                    <li data-id="element-274">
                      Technical issues preventing ebook access that cannot be
                      resolved.
                    </li>
                    <li data-id="element-275">Accidental duplicate purchases.</li>
                  </ul>
                  <p data-id="element-276">
                    Contact us via WhatsApp Business at{' '}
                    <span className="font-medium text-gray-800" data-id="element-277">
                      +1 551-254-0741
                    </span>{' '}
                    or email us at{' '}
                    <a href="mailto:info@dreambuk.com" className="text-[#0ABFBC] hover:underline" data-id="element-278">
                      info@dreambuk.com
                    </a>{' '}
                    to initiate a refund request.
                  </p>
                  <h4 className="font-bold text-gray-900" data-id="element-279">
                    4. Product Usage Guidelines
                  </h4>
                  <p data-id="element-280">
                    ANXIETY OFF SWITCH™ is an educational resource intended for
                    informational purposes only. It is not a substitute for
                    professional medical or mental health advice. By using this
                    product, you acknowledge that you are responsible for your
                    own health decisions.
                  </p>
                  <h4 className="font-bold text-gray-900" data-id="element-281">
                    5. Intellectual Property
                  </h4>
                  <p data-id="element-282">
                    All content within ANXIETY OFF SWITCH™ is the intellectual
                    property of Dreambuk. Unauthorized reproduction or
                    distribution is strictly prohibited.
                  </p>
                  <h4 className="font-bold text-gray-900" data-id="element-283">6. Contact</h4>
                  <p data-id="element-284">
                    For questions regarding these terms, contact us at
                    info@dreambuk.com or via WhatsApp at +1 5512540741.
                  </p>
                </div>
              </div>
              <div className="bg-gray-50 px-6 py-4 text-center" data-id="element-285">
                <button onClick={() => setIsTermsOpen(false)} className="text-gray-500 hover:text-gray-900 text-sm font-medium transition-colors" data-id="element-286">
                  Close
                </button>
              </div>
            </div>
          </div>
        </>}
    </div>;
}